import { isProd } from '../../../config';
import {
  chargeTypes,
  fundingTypes,
  investmentMethods
} from '../../shared/utils';
import {
  getUserInfo,
  isEmpty,
  parseAmount,
  withDefault
} from '../../../core/util';
import {
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../../core/types/application-state';
import { DataLoader } from '../../../core/types/data-loader';
import { authenticateUser } from '../../shared/services/user-service';
import { getExistingBanks } from '../../shared/services/existing-banks-service';
import {
  detailedFailureInfo,
  getPLAIDFailures,
  getPLAIDAuthenticationFailures,
  plaidResultDefaults
} from '../../shared/plaid-utils';
import {
  BankAccountReference,
  BankFailure
} from '../../../core/types/bank-acount';
import {
  initiateAuthService,
  getModalShownAndResult
} from '../../shared/loaders';
import {
  validationFailure,
  onSuccess as onPlaidSuccess
} from '../../shared/services/external-financial-institution-service';
import { postEVerifyRequest } from '../../shared/services/evy-service';
import { verifyBankRoutingNumber } from '../../shared/services/bank-routing-number-service';
import { getExistingBeneficaries } from '../../shared/services/beneficiaries-service';
import { parseIClassFunds } from '../../shared/summit.utils';
import { analyticsDataLayer } from '../../../analytics/analytics-helper';

export const loadLoginData: DataLoader = async (
  _,
  flowProps
): Promise<StateStoreOrVoid> => {
  const authDetails = await initiateAuthService();

  const queryParams = window.location.search;
  return {
    authenticated: authDetails?.authenticated,
    accountType: flowProps?.accountType,
    ...(!isEmpty(queryParams) && {
      routeData: {
        queryString: queryParams
      }
    }),
    app: {}
  };
};

export const commitNewInvestmentsBYOP: DataLoader = ({
  store,
  chosen,
  result
}) => {
  const { modalShown, updateToIClass } = getModalShownAndResult(
    result,
    store?.updateToIClassModalShown
  );

  const chosenInvestments = parseIClassFunds(
    chosen,
    updateToIClass,
    store?.summitData
  );

  const newInvestments = chosenInvestments.map(
    ({ ticker, trpCode, unlockedFund }) => ({
      ticker,
      trpCode,
      fundingAmount: 0,
      unlockedFund: updateToIClass || unlockedFund
    })
  );

  const totalAmount =
    chosenInvestments?.length > 0
      ? parseAmount(
          chosenInvestments
            .reduce((accumulator, { amount }) => {
              return accumulator + parseFloat(amount);
            }, 0)
            .toFixed(2)
        )
      : store?.amount;

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    app: {
      newInvestments,
      investmentMethod: investmentMethods.BYOP
    },
    amount: totalAmount,
    chosenInvestments,
    updateToIClassModalShown: modalShown,
    ...(store.chargeType === chargeTypes.TRANSFER && {
      fundingMethodType: fundingTypes.CHECK
    })
  };
};

export const commitBeneficiaries: DataLoader = ({
  chosen,
  result
}): StateStore => {
  const newBeneficiaries = chosen?.beneficiaries;
  const incompleteBeneficiaries = result?.beneficiaries;

  if (chosen === 'skip')
    return {
      app: {
        beneficiaries: undefined
      }
    };

  if (chosen === 'dangerouslySkipValidation:open-modal') {
    return {
      app: {
        beneficiaries: undefined
      },
      ...(incompleteBeneficiaries && {
        incompleteBeneficiaries
      })
    };
  }

  return {
    incompleteBeneficiaries: undefined,
    app: {
      ...(newBeneficiaries && {
        beneficiaries: newBeneficiaries
      })
    }
  };
};

export const commitFundingMethodType: DataLoader = ({
  chosen = ''
}): StateStore => {
  // manual entry (evy) and link (plaid) should both be funding method type EFT
  const fundingType =
    chosen === fundingTypes.EFT || chosen === fundingTypes.MANUAL
      ? fundingTypes.EFT
      : fundingTypes.CHECK;

  return {
    app: { fundingMethodType: fundingType }
  };
};

export const commitExistingBankDetails: DataLoader = async ({
  chosen
}): Promise<StateStoreOrVoid> => {
  // chosen would be the selected existing bank account from ExistingBanksScreen
  // if 'new-bank' option then chosen is a string value; bypass
  if (chosen !== 'new-bank') {
    const updatedApp = {
      bankInformationReference: {
        bankName: chosen?.bankName,
        bankAccountType: chosen?.accountType,
        bankChoiceMethod: fundingTypes.EXISTING_BANK,
        bankAccountNumber: chosen?.accountNumber?.slice(-4),
        bankRoutingNumber: chosen?.routingNumber,
        bankAccountOwners: chosen?.registrationLines,
        existingBankToken: chosen?.token
      },
      fundingMethodType: fundingTypes.EFT
    };

    return {
      app: updatedApp
    };
  }

  // if customer chooses not to fund via existing bank account
  // don't store bank data, and potentially clear out lingering data
  return {
    app: {
      bankInformationReference: undefined
    }
  };
};

export const commitManualBankDetails: DataLoader = async ({
  chosen,
  inputs,
  store
}): Promise<StateStore> => {
  if (chosen === fundingTypes.CHECK)
    return {
      app: {
        fundingMethodType: chosen
      }
    };

  const {
    app: { customer }
  } = store;

  const bankAccountOwners = [
    `${customer?.name?.firstName} ${customer?.name?.lastName}`
  ];

  if (inputs?.additionalName) {
    bankAccountOwners.push(inputs.additionalName);
  }

  const { accountType, accountNumber, routingNumber } = inputs;

  // retrieve bank account information from routing number
  const bankDetailsFromValidRoutingNumber = await verifyBankRoutingNumber(
    routingNumber
  );

  return {
    // evy bank information saved to existing accounts for use on review screen
    existingAccounts: bankDetailsFromValidRoutingNumber,
    evyHarnessType: inputs.harness,
    app: {
      bankInformationReference: {
        bankAccountType: withDefault(accountType, ''),
        bankChoiceMethod: fundingTypes.MANUAL,
        bankAccountNumber: withDefault(accountNumber, ''),
        bankRoutingNumber: withDefault(
          bankDetailsFromValidRoutingNumber?.correctedRoutingNumber,
          routingNumber
        ),
        bankAccountOwners
      },
      fundingMethodType: fundingTypes.EFT
    }
  };
};

export const commitEverifyReference: DataLoader = ({
  store,
  chosen,
  result
}): StateStore => {
  const { app, existingAccounts, loadedData } = store;

  // for bank link failure thru everify, remove bank info reference and add bank failure information
  // bankInformationRefence set to undefined; purged for submission with purgeEmptyFields
  // failure scenario for evy will send evy fail status as result param for next(); details failure
  if (chosen === 'evy-failure') {
    const { bankFailureInformation = [] } = app;
    return {
      app: {
        bankInformationReference: undefined,
        bankFailureInformation: [
          ...bankFailureInformation,
          {
            bankChoiceMethod: fundingTypes.MANUAL,
            bankName: withDefault(existingAccounts?.bankName, ''),
            bankFailureReason: withDefault(result, 'everify failure')
          }
        ]
      }
    };
  }

  // for successfully linking bank thru everify, add manualEntryToken to existing bank information reference
  const { bankInformationReference } = app;

  return {
    app: {
      bankInformationReference: {
        ...bankInformationReference,
        manualEntryToken: loadedData?.evyToken
      }
    }
  };
};

export const loadExistingBanks: DataLoader = async ({ store }) => {
  const { authenticated } = await authenticateUser(store, '');

  let existingBankAccounts = [];

  if (authenticated) existingBankAccounts = await getExistingBanks();

  return {
    existingBankAccounts
  };
};

export const loadUsersExistingBeneficiaries: DataLoader = async ({ store }) => {
  const { enabled, loadedData = {} } = store;
  // if an existing customer then check if beneficiaries exist
  if (enabled) {
    const existingBeneficiaries = await getExistingBeneficaries();

    return {
      loadedData: { existingBeneficiaries }
    };
  }

  return { loadedData };
};

// @Abhinav - fix complexity
/* eslint-disable complexity */
export const commitBankAccount: DataLoader = async (
  appState
): Promise<PartialStateStore> => {
  const { chosen, result: onSuccessPayload, store } = appState;
  const { bankFailureInformation = [] } = store.app;
  const updatedFailureInformation = bankFailureInformation;

  if (chosen === 'on-success' && onSuccessPayload) {
    const {
      institution,
      account: { id: refId }
    } = onSuccessPayload.metadata;
    const { authenticationId } = onSuccessPayload;

    const accountsFromEFI = await onPlaidSuccess(onSuccessPayload);
    const relevantAccount = accountsFromEFI.find(
      acc => acc.referenceId === refId
    );
    const { bankName, ownerNames } = relevantAccount;
    const failures = getPLAIDFailures(appState, relevantAccount);
    const failureInfo = detailedFailureInfo(failures, institution);
    if (failures) {
      // checks if invesment amt exceeds the bank account balance
      if (failures?.validationBalanceFail) {
        /**
         * in the event of a validation failure
         * log to the efi validation-failure  endpoint
         * this is useful for analysis on what's holding
         * back our users.
         *
         * @ref: US239281
         */
        validationFailure({ ...failures, bankName });
        return {
          app: {
            bankInformationReference: undefined
          },
          loadedData: {
            plaidResult: {
              ...plaidResultDefaults,
              success: false,
              amountTooLow: true
            }
          }
        };
      }
      /**
       * in the event of a validation failure
       * log to the efi validation-failure  endpoint
       *
       * @ref: US239281
       */
      validationFailure({ ...failures, bankName });
      const bankFailure = BankFailure.check({
        bankName,
        bankChoiceMethod: fundingTypes.EXTERNAL,
        bankFailureReason: failureInfo?.failureInfo?.bankFailureReason
      });
      updatedFailureInformation.push(bankFailure);

      return {
        plaidFailures: failures,
        app: {
          bankInformationReference: undefined,
          bankFailureInformation: updatedFailureInformation
        },
        loadedData: {
          plaidResult: {
            ...plaidResultDefaults,
            success: false,
            ...failureInfo
          }
        }
      };
    }

    const { name: custodianName, institution_id: csdId } = institution;
    const externalAccounts = [
      {
        custodianName,
        authenticationId,
        ...relevantAccount
      }
    ];
    const custodians = [
      {
        custodianName,
        name: custodianName,
        id: csdId,
        csdId
      }
    ];

    const [externalAccount] = externalAccounts;
    const bankInformationReference = BankAccountReference.check({
      bankAccountType: externalAccount.accountType,
      bankChoiceMethod: fundingTypes.EXTERNAL,
      bankAccountOwners: ownerNames,
      externalProviderToken: externalAccount.referenceId,
      externalProviderAuthenticationToken: externalAccount.authenticationId
    });

    return {
      app: {
        bankInformationReference
      },
      loadedData: {
        plaidResult: {
          custodians,
          ...plaidResultDefaults,
          externalAccount,
          success: true
        }
      }
    };
  }
  // check if the PLAID linking failed due to invalid creds or MFA
  if (
    appState?.result?.errorCode === 'INVALID_CREDENTIALS' ||
    appState?.result?.errorCode === 'INVALID_MFA'
  ) {
    const authenticationFailures = getPLAIDAuthenticationFailures(
      appState?.result?.errorCode
    );
    const failedBankName = appState?.result?.institution?.name;

    /**
     * in the event of a validation failure
     * log to the efi validation-failure  endpoint
     *
     * @ref: US239281
     */
    validationFailure({ ...authenticationFailures, bankName: failedBankName });
    return {
      app: {
        bankInformationReference: undefined
      },
      loadedData: {
        plaidResult: {
          ...plaidResultDefaults,
          success: false
        }
      }
    };
  }
  return {
    app: {
      bankInformationReference: undefined
    },
    loadedData: {
      plaidResult: {
        ...plaidResultDefaults,
        success: false
      }
    }
  };
};
/* eslint-enable complexity */

export const loadEverifyRequest = async ({
  store
}): Promise<PartialStateStore> => {
  const { app, evyHarnessType, accountType } = store;
  const { trustee, customer, profileApplicationID } = app;
  const { name, residentialAddress, dob, taxID } = getUserInfo({
    accountType,
    trustee,
    customer
  });

  const payload = {
    applicationID: profileApplicationID,
    verificationRequestAddress: {
      street: withDefault(residentialAddress?.street1, ''),
      city: withDefault(residentialAddress?.city, ''),
      stateCode: withDefault(residentialAddress?.state, ''),
      zipCode: withDefault(residentialAddress?.zip, '')
    },
    verificationRequestCustomer: {
      firstName: withDefault(name?.firstName, ''),
      lastName: withDefault(name?.lastName, ''),
      middleName: withDefault(name?.middleName, undefined),
      dateOfBirth: withDefault(dob, ''),
      taxID: withDefault(taxID, '')
    },
    ...(!isProd() && { simulatorModeType: evyHarnessType })
  };

  const { token: evyToken } = await postEVerifyRequest(payload);

  return {
    loadedData: { evyToken }
  };
};
